<script lang="ts">
  import { parseGedcom, buildGedcom } from '../lib/gedcom';
  import { currentTree, currentPeople, currentRels } from '../lib/stores';
  import { db } from '../lib/db';
  import { logAudit } from '../lib/audit';

  let fileInput: HTMLInputElement;
  let phase: 'idle' | 'parsing' | 'success' | 'partial' | 'error' = 'idle';
  let summary = '';
  let warnings: string[] = [];
  let detail = '';

  function reset() {
    phase = 'idle';
    summary = '';
    warnings = [];
    detail = '';
  }

  async function onImport(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!$currentTree) {
      phase = 'error';
      summary = 'No tree open';
      detail = 'Create or open a tree first, then import the GEDCOM into it.';
      input.value = '';
      return;
    }
    phase = 'parsing';
    summary = `parsing ${file.name} (${Math.round(file.size / 1024)} kb)…`;
    warnings = [];
    detail = '';

    try {
      const raw = await file.text();
      const result = parseGedcom(raw, $currentTree.id);
      // Always log diagnostics — helps debug user-supplied files quickly.
      // eslint-disable-next-line no-console
      console.log('[GEDCOM import]', {
        file: file.name,
        bytes: file.size,
        ...result.stats,
        people: result.people.length,
        relationships: result.relationships.length,
        warnings: result.warnings,
      });

      if (result.people.length === 0) {
        phase = 'error';
        summary = 'No individuals found in this file';
        warnings = result.warnings.length
          ? result.warnings
          : [`Parsed ${result.stats.totalLines} GEDCOM lines but no individuals were extracted.`];
        detail =
          'Check the file in a plain-text editor — GEDCOM 5.5.1 records start with lines like ' +
          '"0 @I1@ INDI". If you exported from another tool and it looks unusual, share the file ' +
          'and we can extend the parser. Diagnostics also logged in the browser console.';
        input.value = '';
        return;
      }

      await db.transaction('rw', db.people, db.relationships, async () => {
        await db.people.bulkAdd(result.people);
        await db.relationships.bulkAdd(result.relationships);
      });
      await logAudit('merge.apply', {
        treeId: $currentTree.id,
        meta: {
          source: 'gedcom-import',
          file: file.name,
          added: { people: result.people.length, rels: result.relationships.length },
          stats: result.stats,
        },
      });
      phase = result.warnings.length ? 'partial' : 'success';
      summary = `+${result.people.length} ${result.people.length === 1 ? 'person' : 'people'} · +${result.relationships.length} link${result.relationships.length === 1 ? '' : 's'}`;
      warnings = result.warnings;
      detail = '';
    } catch (e) {
      phase = 'error';
      summary = 'Import failed';
      detail = (e as Error).message ?? String(e);
      warnings = [];
      // eslint-disable-next-line no-console
      console.error('[GEDCOM import] failed', e);
    } finally {
      input.value = '';
      // Auto-clear success after a short window; leave errors/warnings until dismissed.
      if (phase === 'success') {
        setTimeout(reset, 5000);
      }
    }
  }

  function onExport() {
    if (!$currentTree) return;
    const ged = buildGedcom($currentPeople, $currentRels);
    const blob = new Blob([ged], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${$currentTree.title.replace(/\s+/g, '-').toLowerCase()}.ged`;
    a.click();
    URL.revokeObjectURL(url);
  }
</script>

<div class="row">
  <input type="file" accept=".ged,.gedcom,text/plain" bind:this={fileInput}
         on:change={onImport} hidden />
  <button class="small" on:click={() => fileInput.click()} title="Import GEDCOM 5.5.1">↓ import</button>
  <button class="small" on:click={onExport} disabled={!$currentPeople.length} title="Export GEDCOM 5.5.1">↑ export</button>
  {#if phase === 'parsing'}
    <span class="chip signal">{summary}</span>
  {:else if phase === 'success'}
    <span class="chip ok-chip">{summary}</span>
  {/if}
</div>

{#if phase === 'partial' || phase === 'error'}
  <div class="banner {phase}" role="alert">
    <div class="row banner-head">
      <span class="pill {phase === 'error' ? 'danger' : 'gold'}">
        {phase === 'error' ? 'import failed' : 'imported with warnings'}
      </span>
      <strong>{summary}</strong>
      <button class="ghost small banner-close" on:click={reset} aria-label="Dismiss">✕</button>
    </div>
    {#if warnings.length}
      <ul class="warns">
        {#each warnings as w}
          <li>{w}</li>
        {/each}
      </ul>
    {/if}
    {#if detail}
      <p class="dim small detail">{detail}</p>
    {/if}
  </div>
{/if}

<style>
  .banner {
    position: fixed;
    bottom: 18px; right: 18px;
    max-width: 480px;
    z-index: 60;
    padding: 14px 16px 14px 16px;
    border-radius: var(--ls-radius-lg);
    background: var(--ls-surface);
    border: 1px solid var(--ls-border);
    box-shadow: var(--ls-shadow-lg);
    font-size: 13px;
  }
  .banner.error { border-color: color-mix(in srgb, var(--ls-danger) 60%, var(--ls-border)); }
  .banner.partial { border-color: color-mix(in srgb, var(--ls-accent) 60%, var(--ls-border)); }
  .banner-head { gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 8px; }
  .banner-head strong { color: var(--ls-ink); font-weight: 600; }
  .banner-close { margin-left: auto; }
  .warns { margin: 8px 0 0; padding-left: 18px; color: var(--ls-text); display: flex; flex-direction: column; gap: 4px; }
  .warns li { font-size: 12px; line-height: 1.5; }
  .detail { margin: 8px 0 0; line-height: 1.5; }
  .chip.ok-chip {
    color: var(--ls-ok);
    background: color-mix(in srgb, var(--ls-ok) 14%, transparent);
    border-color: color-mix(in srgb, var(--ls-ok) 40%, transparent);
  }
</style>
